import Helpers from '@Apis/helpers';
/**
 * Retrieves the output materials from the jobWorks array and organizes them based on product_sku_id.
 * @param {Array} jobWorks - The array of jobWorks objects.
 * @returns {Object} - The updated jwInfo object with the output materials as an array.
 */
export const getPoLinesFromJobWorks = (jobWorks, issueType) => {
  // Create an empty object to store the output materials
  const outputMaterials = {};

  // Get the first jobWorks object from the array
  const jwInfo = jobWorks[0];
  if (jwInfo && issueType !== 'INPUT') {
    jwInfo.mo_fg_id = null;
  }
  // Helper function to process the row materials and update the outputMaterials object
  const getJwLines = (rowMaterials, row) => {
    // Loop through the rowMaterials array
    for (let i = 0; i < rowMaterials?.length; i++) {
      // Check if the outputMaterials object already has a property with the same product_sku_id
      if (outputMaterials.hasOwnProperty(rowMaterials[i]?.product_sku_id)) {
        // If the property exists, update the required_output_qty and jw_wise_breakup properties
        outputMaterials[rowMaterials[i]?.product_sku_id] = {
          ...outputMaterials[rowMaterials[i]?.product_sku_id],
          required_output_qty: (outputMaterials[rowMaterials[i]?.product_sku_id]?.required_output_qty ?? outputMaterials[rowMaterials[i]?.product_sku_id]?.required_processing_qty) + (rowMaterials[i]?.required_output_qty ?? rowMaterials[i]?.required_processing_qty),
          jw_wise_breakup: [...outputMaterials[rowMaterials[i]?.product_sku_id]?.jw_wise_breakup, {
            pr_line_output_material_id: rowMaterials[i]?.prl_output_material_id,
            pr_line_processing_material_id: rowMaterials[i]?.prl_processing_material_id,
            item_mo_line_id: rowMaterials[i]?.mo_line_id,
            item_mo_fg_id: rowMaterials[i]?.mo_fg_id,
            quantity: Number(rowMaterials[i]?.required_output_qty ?? rowMaterials[i]?.required_processing_qty),
            production_route_line_id: rowMaterials[i]?.production_route_line_id,
          }],
        };
      } else {
        // If the property doesn't exist, create a new entry in the outputMaterials object
        outputMaterials[rowMaterials[i]?.product_sku_id] = {
          ...rowMaterials[i],
          required_output_qty: Number(rowMaterials[i]?.required_output_qty ?? rowMaterials[i]?.required_processing_qty),
          jw_wise_breakup: [{
            pr_line_output_material_id: rowMaterials[i]?.prl_output_material_id,
            pr_line_processing_material_id: rowMaterials[i]?.prl_processing_material_id,
            item_mo_line_id: rowMaterials[i]?.mo_line_id,
            item_mo_fg_id: rowMaterials[i]?.mo_fg_id,
            quantity: Number(rowMaterials[i]?.required_output_qty ?? rowMaterials[i]?.required_processing_qty),
            production_route_line_id: rowMaterials[i]?.production_route_line_id,
          }],
        };
      }
    }
  };

  // Loop through each jobWorks object
  jobWorks?.map((row) => {
    // Process the output materials and processing materials for each jobWorks object
    if(issueType === 'INPUT') {
      getJwLines(row?.output_materials, row);
    } else { 
      getJwLines(row?.processing_materials, row);  
    }
    
  })

  // Return the updated jwInfo object with the output materials as an array
  return {
    ...jwInfo,
    output_materials: Object.values(outputMaterials),
  }
}


/**
 * Retrieves the invoice lines from the jobWorks array and organizes them based on product_sku_id.
 * @param {Array} jobWorks - The array of jobWorks objects.
 * @returns {Object} - The updated jwInfo object with the invoice lines as an array.
 */
export const getInvoiceLinesFromJobWorks = (jobWorks, issueType) => {
  // Create an empty object to store the invoice lines
  const inputMaterials = {};

  // Get the first jobWorks object from the array
  const jwInfo = jobWorks[0];

  // Helper function to process the row materials and update the inputMaterials object
  const getJwLines = (rowMaterials, row) => {
    // Loop through the rowMaterials array
    for (let i = 0; i < rowMaterials?.length; i++) {
      let rowMaterial = rowMaterials[i];
      // Check if the inputMaterials object already has a property with the same product_sku_id
      if (inputMaterials.hasOwnProperty(rowMaterial?.product_sku_id)) {
        // If the property exists, update the required_input_qty and jw_wise_breakup properties

        // Extract necessary variables for readability
        const productSkuId = rowMaterial?.product_sku_id;
        const requiredInputQty = rowMaterial?.required_input_qty ?? rowMaterial?.required_processing_qty;
        const inputMaterialsQty = inputMaterials[productSkuId]?.required_input_qty ?? inputMaterials[productSkuId]?.required_processing_qty;
        const totalInvGenQty = Number(rowMaterial?.total_inv_gen_qty ?? 0);

        // Calculate remaining_quantity
        const remainingQty = inputMaterialsQty + requiredInputQty;

        inputMaterials[rowMaterial?.product_sku_id] = {
          ...inputMaterials[rowMaterial?.product_sku_id],
          required_input_qty: inputMaterialsQty + requiredInputQty,
          mo_line_id: rowMaterial?.mo_line_id_for_department,
          mo_fg_id: rowMaterial?.mo_fg_id_for_department,
          jw_wise_breakup: [...inputMaterials[rowMaterial?.product_sku_id]?.jw_wise_breakup, {
            pr_line_input_material_id: rowMaterial?.prl_input_material_id,
            pr_line_processing_material_id: rowMaterial?.prl_processing_material_id,
            item_mo_line_id: rowMaterial?.mo_line_id,
            item_mo_fg_id: rowMaterial?.mo_fg_id,
            quantity: Number(rowMaterial?.required_input_qty ?? rowMaterial?.required_processing_qty),
            production_route_line_id: rowMaterial?.production_route_line_id,
          }],
        }
      } else {
        // If the property doesn't exist, create a new entry in the inputMaterials object
        inputMaterials[rowMaterial?.product_sku_id] = {
          hsn_code: rowMaterial?.hsn_code,
          mo_line_id: rowMaterial?.mo_line_id_for_department,
          mo_fg_id: rowMaterial?.mo_fg_id_for_department,
          batchConsumptionMethod: rowMaterial?.default_outwards_method || Helpers.getBatchMethod(rowMaterial),
          product_batches: rowMaterial?.product_batches,
          product_sku_info: {
            product_sku_id: rowMaterial?.product_sku_id,
            product_sku_name: rowMaterial?.product_sku_name,
            ref_product_code: rowMaterial?.ref_product_code,
            internal_sku_code: rowMaterial?.internal_sku_code,
            system_custom_fields: rowMaterial?.system_custom_fields ?? [],
            uom_info: rowMaterial?.uom_info,
            product_type: rowMaterial?.product_type,
            expiry_days: rowMaterial?.expiry_days,
            manufacturing_date_format: rowMaterial?.manufacturing_date_format,
            expiry_date_format: rowMaterial?.expiry_date_format,
          },
          required_input_qty: Number(rowMaterial?.required_input_qty ?? rowMaterial?.required_processing_qty),
          remaining_quantity: Number(rowMaterial?.required_input_qty ?? rowMaterial?.required_processing_qty) - Number(rowMaterial?.total_inv_gen_qty ?? 0),
          product_sku_name: rowMaterial?.product_sku_name,
          product_sku_id: rowMaterial?.product_sku_info?.product_sku_id,
          unitPrice: rowMaterial?.selling_price || 0,
          taxId: rowMaterial?.tax_info?.tax_id,
          tax_id: rowMaterial?.tax_info?.tax_id,
          taxInfo: rowMaterial?.tax_info,
          tax_info: rowMaterial?.tax_info,
          discount: 0,
          tenant_product_id: rowMaterial?.tenant_product_id,
          uomId: rowMaterial?.uom_info?.uom_id,
          uomInfo: rowMaterial?.uom_info,
          uom_info: rowMaterial?.uom_info,
          expiry_days: rowMaterial?.expiry_days,
          jw_wise_breakup: [{
            pr_line_input_material_id: rowMaterial?.prl_input_material_id,
            pr_line_processing_material_id: rowMaterial?.prl_processing_material_id,
            item_mo_line_id: rowMaterial?.mo_line_id,
            item_mo_fg_id: rowMaterial?.mo_fg_id,
            quantity: Number(rowMaterial?.required_input_qty ?? rowMaterial?.required_processing_qty),
            production_route_line_id: rowMaterial?.production_route_line_id,
          }],
        };
      }
    }
  }

  // Loop through each jobWorks object
  jobWorks?.map((row) => {
    // Process the input materials and processing materials for each jobWorks object
    if(issueType === 'INPUT') {
      getJwLines(row?.input_materials, row);
    } else {
      getJwLines(row?.processing_materials, row);
    }
  })

  // Return the updated jwInfo object with the invoice lines as an array
  return {
    ...jwInfo,
    input_materials: Object.values(inputMaterials),
  }
}